// A webes alkalmazás belépési pontja.
//
// Felépítés: hitelesítés-kapu -> váz -> képernyő. A képernyők a tervek
// sorrendjében épülnek be (docs/05-frontend-ujraepites-terv.md).

import './styles/app.css';

import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, functions, storage, usingEmulator } from './lib/firebase';
import { detectLanguage, loadDictionary, t } from './lib/i18n';
import { h } from './lib/dom';
import { loginPage } from './pages/login';
import { registerPage } from './pages/register';
import { confirmDialog, openDialog } from './lib/dialog';
import {
  createCompanyUser,
  deleteCompanyUser,
  setSeatTier,
  type UserAdminContext,
} from './lib/user-admin';
import {
  deleteFieldDefinition,
  deleteGroup,
  deleteJobTitle,
  saveAutoUserPush,
  saveField,
  saveUserProfile,
  syncUserGroups,
  saveGroup,
  saveJobTitle,
  type MasterDataContext,
} from './lib/master-data';
import { createShell, defaultNavItems, type Shell } from './lib/shell';
import {
  loadCompany,
  loadSessionUser,
  roleOf,
  type SessionUser,
  type SubscriptionView,
} from './lib/session';
import {
  loadAllCompanyBilling,
  loadSeatUsage,
  startCheckout,
  type CompanyBilling,
} from './lib/billing';
import { loadPeople, loadProjects, type PersonView, type ProjectView, type TaskTab } from './lib/data';
import { tasksPage } from './pages/tasks';
import {
  attachUploadedFiles,
  removeAttachment,
  saveSignature,
  uploadAttachment,
  uploadProfilePhoto,
} from './lib/uploads';
import { applyFilter, emptyFilter, filterPanel, type TaskFilter } from './pages/filters';
import { projectDetailPage, projectListPage, type ProjectFilterKind } from './pages/projects';
import {
  openNewProjectWizard,
  type NewProjectInput,
  openRecurrenceDialog,
  openTemplateEditor,
} from './pages/project-wizard';
import { calendarPage, type CalendarMode } from './pages/calendar';
import {
  groupsPage,
  jobTitlesPage,
  openGroupDialog,
  openPersonPicker,
  openUserDialog,
  peoplePage,
  profilePage,
  type GroupView,
  type Role,
  type UserInput,
} from './pages/people';
import {
  fieldsPage,
  openFieldEditor,
  notificationsPage,
  settingsPage,
  subscriptionsPage,
  type FieldDefinition,
} from './pages/admin';
import { numberField } from './lib/fields';
import {
  reloadProject,
  loadFieldDefinitions,
  loadGroups,
  loadJobTitles,
  loadNotifications,
  loadProjectTemplates,
  loadRoles,
} from './lib/data';
import { landingPage } from './pages/landing';
import { privacyPage, termsPage } from './pages/legal';
import { contactPage } from './pages/contact';
import { sutiBannerMegjelenites } from './pages/cookie-banner';
import { appBannerMegjelenites } from './pages/app-banner';
import { metaEsemeny, metaPixelBekotes } from './lib/meta-pixel';
import { gaBekotes } from './lib/google-analytics';
import { adjAiHozzajarulast, kerjAiHozzajarulast, vanAiHozzajarulas, vondVisszaAiHozzajarulast } from './lib/ai-consent';
import { dashboardPage } from './pages/dashboard';
import { aiCapturePage, type AiProjectDraft } from './pages/ai-capture';
import { httpsCallable } from 'firebase/functions';
import { reportsPage } from './pages/reports';
import { searchOverlay, type SearchOverlayHandle } from './pages/search';
import { printableProject } from './pages/print';
import { errorPage, loadingPage as authLoadingPage, passwordResetPage } from './pages/auth-extra';
import { taskDetail } from './pages/task-detail';
import {
  openAcceptDialog,
  openStatusDialog,
  openTimeOverrideDialog,
} from './pages/task-dialogs';
import {
  openAttachmentDialog,
  openCommentDialog,
  openDynamicFieldsDialog,
  openNewTaskDialog,
  openReopenDialog,
  openSignatureDialog,
} from './pages/task-forms';
import { priorityValue, type TaskView } from './lib/model';
import { doc, onSnapshot, waitForPendingWrites } from 'firebase/firestore';
import {
  disablePush,
  enablePush,
  hasVapidKey,
  isPushAvailable,
  onPushInForeground,
  permissionState,
} from './lib/push';
import { db } from './lib/firebase';
import {
  acceptTask as writeAccept,
  addComment as writeComment,
  addTaskToProject,
  createProject,
  changeTaskStatus,
  overrideTaskTime,
  startTaskTimer,
  stopTaskTimer,
  markNotificationsSeen,
  reopenTask,
  saveProjectTemplate,
  setProjectRecurrence,
  saveTaskFields,
  type WriteContext,
} from './lib/writes';

const root = document.querySelector<HTMLDivElement>('#root');
if (!root) throw new Error(t('app.missingRoot'));

/// Sötét téma: a rendszer beállítását követjük, amíg nincs saját kapcsoló.
function applyTheme(): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  root!.classList.toggle('dark', prefersDark);
}
applyTheme();
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTheme);

function emulatorBanner(): HTMLElement {
  return h('div', {
    class: 'offline',
    text: t('app.emulatorBanner'),
  });
}

function loadingScreen(): HTMLElement {
  return h(
    'div',
    {
      style: {
        flexGrow: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    h('div', { class: 'ring' }),
  );
}

function render(children: HTMLElement): void {
  root!.replaceChildren(...(usingEmulator ? [emulatorBanner()] : []), children);
}

/// Ideiglenes tartalom, amíg az adott képernyő meg nem épül.
///
/// Szándékosan megmondja, hol tartunk: így a váz kipróbálható anélkül, hogy
/// félkész képernyőket mutatnánk késznek.
function comingSoon(title: string): HTMLElement {
  return h(
    'div',
    { style: { padding: '24px' } },
    h(
      'div',
      { class: 'empty card' },
      h('h2', { class: 'h2', text: title }),
      h('p', {
        class: 'body ts',
        style: { maxWidth: '460px' },
        text: t('app.comingSoon'),
      }),
    ),
  );
}

/// Az utvonalak cimeinek KULCSAI. Kesz szoveget nem tarolhat modul szintu
/// tabla: a szotar csak a render elott erkezik meg.
const routeTitleKeys: Record<string, string> = {
  dashboard: 'nav.dashboard',
  tasks: 'nav.tasks',
  'tasks/open': 'app.routeTasksOpen',
  'tasks/mine': 'nav.tasksMine',
  'tasks/previous': 'app.routeTasksPrevious',
  'tasks/all': 'app.routeTasksAll',
  projects: 'nav.projects',
  calendar: 'nav.calendar',
  people: 'nav.people',
  'people/groups': 'people.tabGroups',
  'people/jobTitles': 'people.tabJobTitles',
  'people/profile': 'nav.profile',
  ai: 'ai.webTitle',
  reports: 'report.title',
  settings: 'general.settings',
  notifications: 'noti.title',
  subscriptions: 'billing.overviewTitle',
};

function currentRoute(): string {
  const hash = window.location.hash.replace(/^#\/?/, '');
  return hash.length > 0 && hash in routeTitleKeys ? hash : 'tasks/open';
}

function mountApp(sessionUser: SessionUser, devData?: {
  projects: ProjectView[];
  people: PersonView[];
  /// CSAK a fejlesztoi elonezetnek: igy a `.pending` jeloles (4.2) elo,
  /// offline iras nelkul is megnezheto a felületen.
  pendingTaskIds?: string[];
}): void {
  let shell: Shell;

  // A projektek es az emberek egyszer toltodnek be, es a fulek kozott mar nem
  // kerdezzuk le ujra: a szures kliensoldalon tortenik, ahogy az appban is.
  const cache: {
    loading: boolean;
    projects: ProjectView[];
    people: PersonView[];
    error?: string;
  } = devData
    ? { loading: false, projects: devData.projects, people: devData.people }
    : { loading: true, projects: [], people: [] };

  const companyId = sessionUser.memberships[0]?.companyId;
  const isAdmin = roleOf(sessionUser.memberships) !== 'user';
  const isSuper = roleOf(sessionUser.memberships) === 'super';

  /// Az elofizetes allapota es a kiosztott helyek. A beallitasok kartyaja es
  /// a szuperadmin attekinto is ebbol dolgozik.
  let subscription: SubscriptionView | undefined;
  let seatUsage: { basic: number; ai: number } | undefined;
  let companyBilling: CompanyBilling[] = [];
  let billingError: string | undefined;

  let selectedTask: TaskView | undefined;

  /// Azok a feladatok, amelyeken indítottunk írást, de a szerver még nem
  /// igazolta vissza (4.2). Ez adja a `.pending` jelölést a sorokon és a
  /// számot a kapcsolat sávjában.
  const pendingTaskIds = new Set<string>(devData?.pendingTaskIds ?? []);

  /// A push-ertesites allapota EZEN a bongeszon (4.3).
  ///
  /// `undefined` = meg nem derult ki; ilyenkor a kartya sem jelenik meg, mert
  /// egy „nem tudom" allapotu kapcsolo rosszabb, mint a semmi.
  let push: { enabled: boolean; blocked?: 'denied' | 'unsupported' | 'missing-key' } | undefined;
  /// A feladatlista szurese. A `filters.ts` panel eddig MEGVOLT, de sehonnan
  /// nem nyilt: a webes feladatlistan egyaltalan nem lehetett szurni.
  let taskFilter: TaskFilter = emptyFilter();
  let projectFilter: ProjectFilterKind = 'all';
  let openProjectId: string | undefined;
  let calendarMode: CalendarMode = 'week';
  let calendarAnchor = new Date();
  /// A beallitasok es az emberek alnezetei — az utvonal masodik szegmense.
  let peopleView: 'list' | 'groups' | 'jobTitles' | 'profile' = 'list';
  let settingsView: 'home' | 'fields' = 'home';
  let groups: GroupView[] = [];
  let jobTitles: { id: string; name: string }[] = [];
  let fields: FieldDefinition[] = [];
  /// A ceg projektsablonjai (`projects/{companyId}/taskTypes`). Eddig NEM
  /// toltottuk be: a varazslo ures sablonlistat kapott, a sablonszerkeszto
  /// pedig csak azt az egy sablont ismerte, amit epp atadtak neki.
  let templates: { id: string; name: string; tasks: { title: string; description?: string }[] }[] = [];
  /// A ceg ferohelykerete (`maxUser`). A W23 lablece ebbol szamolja, hany
  /// hely maradt szabadon.
  let seatLimit: number | undefined;
  /// Az értesítések (`users/{uid}/notifications`). A W33 képernyő eddig
  /// SEHONNAN nem nyílt: se csengő, se útvonal nem vezetett hozzá.
  let notifications: Awaited<ReturnType<typeof loadNotifications>> = [];
  let notificationTab: 'unread' | 'all' = 'unread';
  let roles: Record<string, Role> = {};
  let theme: 'system' | 'light' | 'dark' = 'system';

  /// Az iras kontextusa egy feladathoz: melyik ceg, projekt es AKTUALIS
  /// verzio erintett. A verzio azonositoja a projektbol jon, ezert ha a
  /// feladat projektje nem talalhato, inkabb nem irunk.
  function writeContextFor(task: TaskView): WriteContext | undefined {
    if (!companyId || !task.projectId) return undefined;
    const project = cache.projects.find((p) => p.id === task.projectId);
    if (!project?.currentChangeId) return undefined;
    return {
      db,
      companyId,
      projectId: project.id,
      changeId: project.currentChangeId,
      userId: sessionUser.uid,
    };
  }

  /// Egy iras lefuttatasa egyseges visszajelzessel es ujratoltessel.
  ///
  /// Sikeres iras utan ujratoltunk, mert a valtozas a szerveren tortent: a
  /// Egysegesen olvashato hibaszoveg egy eldobott kivetelbol.
  function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  /// helyi masolat frissitgetese elobb-utobb elcsuszna a valosagtol.
  async function runWrite(
    task: TaskView,
    label: string,
    operation: (ctx: WriteContext) => Promise<unknown>,
  ): Promise<void> {
    const ctx = writeContextFor(task);
    if (!ctx) {
      shell.toast(t('app.writeMissingProject', [label]), 'bad');
      return;
    }

    // 4.2 — FÜGGŐ MENTÉS.
    //
    // A helyi tárral az írás AZONNAL érvényesül a gyorsítótárban, a
    // visszaadott ígéret viszont csak a SZERVER visszaigazolásakor teljesül.
    // Offline ez akár órákig nem következik be, ezért nem várhatjuk meg:
    // a felhasználó addig egy örökké pörgő gombot nézne, és azt hinné,
    // elveszett a munkája. A változás addig `.pending` jelölést kap.
    pendingTaskIds.add(task.id);
    refreshConnection();

    let settled = false;
    const ack = operation(ctx).then(
      () => {
        settled = true;
        pendingTaskIds.delete(task.id);
        refreshConnection();
      },
      (error: unknown) => {
        settled = true;
        pendingTaskIds.delete(task.id);
        refreshConnection();
        throw error;
      },
    );

    // Offline az ígéretet nem várjuk meg, de a KÉSŐBBI hibát sem nyeljük el:
    // ha a szerver végül visszautasítja (pl. jogosultság), szólunk róla.
    if (!navigator.onLine) {
      void ack.catch((error: unknown) => {
        shell.toast(t('app.writeFailed', [label, messageOf(error)]), 'bad');
      });
    }

    try {
      if (navigator.onLine) {
        await ack;
        shell.toast(t('app.writeSaved', [label]), 'ok');
      } else {
        shell.toast(t('app.savedPending', [label]), 'inf');
        // MEG KELL VARNI, hogy a helyi iras megjelenjen a gyorsitotarban.
        // Enelkul az ujraolvasas MEGELOZI az irast, es a lista a REGI
        // allapotot mutatja: az adatbazisban mar `Folyamatban` all, a
        // kepernyon meg `Elvegzendo`. Ez a hiba CSAK offline jelentkezik,
        // mert online az `await ack` amugy is megvarja a szervert.
        await waitForLocalWrite(ctx);
      }
      // CSAK az érintett projektet olvassuk újra.
      //
      // Korábban itt teljes újratöltés állt: a projektek collection group
      // lekérdezése, a felhasználók, öt törzsadat-lekérdezés, a cég, a
      // kiosztott helyek (ami MÉGEGYSZER végigolvassa a felhasználókat) és az
      // értesítések. Tíz körbefordulás egyetlen státuszváltásért — a mentés
      // ettől érződött másodpercekig „gondolkodónak", és a futó appon mérve
      // tényleg annyi is volt.
      //
      // Egy feladatírás EGYETLEN projekt aktuális verzióját érinti; a
      // törzsadat tőle nem változik. A `cache.loading` SEM kapcsol vissza:
      // azzal a lista előbb csontvázra váltana, aztán vissza — egy már
      // látható lista villogása semmit nem közöl.
      const frissitett = await reloadProject(ctx.companyId, ctx.projectId);
      const helye = cache.projects.findIndex((p) => p.id === ctx.projectId);
      if (frissitett) {
        if (helye >= 0) cache.projects[helye] = frissitett;
        else cache.projects.push(frissitett);
      } else if (helye >= 0) {
        // A projekt eltűnt (törlés): ne hagyjuk a listában.
        cache.projects.splice(helye, 1);
      }
      const tab = (currentRoute().split('/')[1] ?? 'open') as TaskTab;
      renderTasks(tab);
      // A reszletek panel NYITVA marad a frissitett feladaton. Korabban
      // minden mentes bezarta: egy megjegyzes elkuldese utan a felhasznalo
      // visszakerult az ures listara, es ujra ki kellett keresnie a feladatot.
      const refreshed = cache.projects
        .flatMap((project) => project.tasks)
        .find((candidate) => candidate.id === task.id);
      if (refreshed) {
        selectedTask = refreshed;
        renderTasks(tab);
        showDetail(refreshed, tab);
      } else {
        selectedTask = undefined;
        shell.setDetail(null);
      }
    } catch (error) {
      // Offline agon ezt mar az `ack.catch` kezeli; ne mondjuk el ketszer.
      if (settled) shell.toast(t('app.writeFailed', [label, messageOf(error)]), 'bad');
    }
  }

  /// Egy fájl feltöltése a Storage-ba, majd bejegyzése a feladatra.
  ///
  /// Nem a `runWrite`-on megy keresztül, mert a feltöltés HOSSZÚ: a
  /// haladásjelzés a képernyőn fut, és a „mentve" visszajelzés csak a végén
  /// helyénvaló. A hiba viszont ugyanúgy a hívóhoz kerül vissza, hogy a
  /// csatolmány-fül a saját hibasávjában mutathassa meg.
  async function uploadTaskFile(
    task: TaskView,
    file: File,
    onProgress?: (ratio: number) => void,
  ): Promise<void> {
    const ctx = writeContextFor(task);
    if (!ctx) throw new Error(t('app.missingProjectShort'));

    const upload = uploadAttachment(storage, task.id, file, onProgress);
    const uploaded = await upload.promise;
    await attachUploadedFiles(ctx, task.id, [uploaded]);

    shell.toast(t('app.fileUploaded', [file.name]), 'ok');
    cache.loading = true;
    await ensureData();
    const tab = (currentRoute().split('/')[1] ?? 'open') as TaskTab;
    const refreshed = cache.projects
      .flatMap((project) => project.tasks)
      .find((candidate) => candidate.id === task.id);
    renderTasks(tab);
    if (refreshed) {
      selectedTask = refreshed;
      showDetail(refreshed, tab);
    }
  }

  /// Nyitott (nem lezárt) feladatok száma felelősönként.
  function openTaskCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const project of cache.projects) {
      for (const task of project.tasks) {
        if (task.status === 'completed' || task.status === 'failed') continue;
        for (const id of task.assigneeIds) counts[id] = (counts[id] ?? 0) + 1;
      }
    }
    return counts;
  }

  function showDetail(task: TaskView, tab: TaskTab): void {
    shell.setDetail(
      taskDetail(task, cache.people, {
        onClose: () => {
          selectedTask = undefined;
          shell.setDetail(null);
          renderTasks(tab);
        },
        onChangeStatus: (current) =>
          openStatusDialog({
            task: current,
            onSubmit: (input) =>
              void runWrite(current, t('app.opStatusChange'), (ctx) =>
                changeTaskStatus(ctx, current.id, input),
              ),
          }),
        onAcceptDecline: (current) =>
          openAcceptDialog({
            task: current,
            assignerName: current.submittedUserId
              ? cache.people.find((p) => p.id === current.submittedUserId)?.fullName
              : undefined,
            onSubmit: (result) =>
              void runWrite(
                current,
                result.accepted ? t('app.opAccept') : t('app.opDecline'),
                (ctx) => writeAccept(ctx, current.id, result),
              ),
          }),
        onStartTimer: (current) =>
          void runWrite(current, t('app.opStartTimer'), (ctx) =>
            startTaskTimer(ctx, current.id),
          ),
        onStopTimer: (current) =>
          void runWrite(current, t('app.opStopTimer'), (ctx) =>
            stopTaskTimer(ctx, current.id),
          ),
        onOverrideTime: (current) =>
          openTimeOverrideDialog({
            task: current,
            onSubmit: (input) =>
              void runWrite(current, t('app.opOverrideTime'), (ctx) =>
                overrideTaskTime(ctx, current.id, input),
              ),
          }),
        onReopen: (current) =>
          openReopenDialog({
            task: current,
            onSubmit: (input) =>
              void runWrite(current, t('app.opReopen'), (ctx) =>
                reopenTask(ctx, current.id, input),
              ),
          }),
        onComment: (current, payload) => {
          // A W12 ful sajat szerkesztojebol mar kesz szoveg erkezik; a regi,
          // parbeszedablakos ut csak akkor indul, ha nincs payload.
          if (payload) {
            void runWrite(current, t('app.opComment'), (ctx) =>
              writeComment(ctx, current.id, payload),
            );
            return;
          }
          openCommentDialog({
            people: cache.people,
            onSubmit: (comment) =>
              void runWrite(current, t('app.opComment'), (ctx) =>
                writeComment(ctx, current.id, comment),
              ),
          });
        },
        // A csatolmany-ful (W13) sajat ejtozonaval es tallozoval dolgozik; a
        // regi parbeszedablak csak onnan indul, ahol nincs ful (pl. a „…" menu).
        onAttach: (current) =>
          openAttachmentDialog({
            onSubmit: (files) => {
              void (async () => {
                for (const file of files) {
                  await uploadTaskFile(current, file);
                }
              })();
            },
          }),
        onUpload: (current, file, onProgress) => uploadTaskFile(current, file, onProgress),
        onRemoveAttachment: (current, url) =>
          void runWrite(current, t('app.opRemoveAttachment'), (ctx) =>
            removeAttachment({ ...ctx, storage }, current.id, url),
          ),
        onSign: (current) =>
          openSignatureDialog({
            previousUrl: current.signatureUrl,
            onSubmit: (dataUrl) =>
              void runWrite(current, t('app.opSign'), (ctx) =>
                saveSignature({ ...ctx, storage }, current.id, dataUrl),
              ),
          }),
        onSaveFields: (current, values) =>
          void runWrite(current, t('app.opFields'), (ctx) =>
            saveTaskFields(ctx, current.id, values),
          ),
        onEditFields: () =>
          openDynamicFieldsDialog({
            // A cegszintu mezodefiniciok betoltese a beallitasok keperyzovel
            // egyutt kerul be; addig a sema uresen is helyesen viselkedik.
            schema: [],
            values: {},
            onSubmit: () => shell.toast(t('app.fieldsMissingSchema'), 'inf'),
          }),
      }),
    );
  }

  function renderTasks(tab: TaskTab): void {
    shell.setContent(
      tasksPage(cache, {
        tab,
        userId: sessionUser.uid,
        filter: taskFilter,
        // Allapotvaltas KOZVETLENUL a sorbol. Ugyanaz a parbeszed nyilik, mint
        // a reszletek panelbol — nem masodik ut, csak rovidebb.
        onChangeStatus: (task) =>
          openStatusDialog({
            task,
            onSubmit: (input) =>
              void runWrite(task, t('app.opStatusChange'), (ctx) =>
                changeTaskStatus(ctx, task.id, input),
              ),
          }),
        onFilterChange: (next) => {
          taskFilter = next;
          renderTasks(tab);
        },
        onOpenFilters: () => {
          openDialog({
            title: t('filter.shortTitle'),
            // A terv (W17) 380 px-es oldalso fiokot mutat a jobb szelen,
            // hogy a feladatlista vegig lathato maradjon mogotte.
            placement: 'right',
            width: '380px',
            body: (close) => {
              // A panel a talalatszamot is mutatja, ezert minden valtozas utan
              // ujra kell rajzolni — kulonben a szam megallna az elso
              // allapotnal.
              const host = h('div', { class: 'col' });
              const draw = () => {
                host.replaceChildren(
                  filterPanel({
                    filter: taskFilter,
                    people: cache.people,
                    projects: cache.projects,
                    resultCount: applyFilter(
                      cache.projects.flatMap((p) => p.tasks),
                      taskFilter,
                      { userId: sessionUser.uid },
                    ).length,
                    onChange: (next) => {
                      taskFilter = next;
                      draw();
                      renderTasks(tab);
                    },
                    showHeader: false,
                    onClose: close,
                  }),
                );
              };
              draw();
              return host;
            },
          });
        },
        onTabChange: (next) => {
          window.location.hash = `/tasks/${next}`;
        },
        onNewTask: () => startNewTask(),
        selectedTaskId: selectedTask?.id,
        onTaskSelected: (task) => {
          selectedTask = task;
          renderTasks(tab);
          showDetail(task, tab);
        },
      }),
    );
  }

  /// Uj feladat felvetele.
  ///
  /// KOZOS fuggveny, mert ket helyrol indul: a feladatlista „Uj feladat"
  /// gombjarol es a naptar napjaira tett „+"-rol. Az utobbi at is adja a
  /// kivalasztott napot hataridonek — kulonben a felhasznalonak ujra be
  /// kellene irnia azt, amit epp kijelolt.
  function startNewTask(deadline?: Date): void {
    // Uj feladat mindig egy PROJEKTBE kerul; a kivalasztott feladat
    // projektje adja a celt, enelkul a projektlista kepernyorol lehet
    // majd inditani.
    const target = selectedTask ?? cache.projects.flatMap((p) => p.tasks)[0];
    if (!target) {
      shell.toast(t('app.pickProjectFirst'), 'inf');
      return;
    }
    openNewTaskDialog({
      // A naptarbol indulva ez a kivalasztott nap; a feladatlistarol
      // `undefined`, es a mezo uresen marad.
      deadline,
      people: cache.people,
      projectTitle: target.projectTitle,
      groups,
      // A NYITOTT feladatok szama emberenkent — ez a terheles a kozos
      // embervalasztoban (W28). Eddig sehol nem latszott, kire mennyi jut.
      openCounts: openTaskCounts(),
      // A ceg mezodefinicioi mar be vannak toltve (`loadFieldDefinitions`),
      // de az uj feladatnal eddig nem lehetett kitolteni oket.
      fieldSchema: fields.map((f) => ({ id: f.id, name: f.name, type: f.type })),
      onSubmit: (input) =>
        void runWrite(target, t('app.opNewTask'), (ctx) =>
          addTaskToProject(ctx, {
            title: input.title,
            description: input.description,
            priority: priorityValue(input.priority),
            deadline: input.deadline,
            assigneeIds: input.assigneeIds,
            followerUserId: input.followerUserId,
            fields: Object.entries(input.fieldValues).map(([id, value]) => {
              const definition = fields.find((f) => f.id === id);
              return {
                id,
                name: definition?.name ?? id,
                type: definition?.type ?? 'text',
                value,
              };
            }),
          }),
        ),
    });
  }
  /// A szuperadmin elofizetes-attekintoje.
  ///
  /// A cegek listazasat a biztonsagi szabaly CSAK szuperadminnak engedi, es a
  /// lekerdezes EGESZBEN hasal el, nem reszlegesen. Ezert a hibat kulon
  /// kezeljük, es meg is mondjuk, mi tortent — egy ures tablazat azt
  /// sugallna, hogy nincs egyetlen ceg sem.
  async function renderSubscriptions(): Promise<void> {
    if (!isSuper) {
      billingError = t('billing.noPermission');
      shell.setContent(subscriptionsPage({ companies: [], error: billingError }));
      return;
    }

    shell.setContent(subscriptionsPage({ companies: companyBilling }));
    try {
      companyBilling = await loadAllCompanyBilling();
      billingError = undefined;
    } catch (error) {
      billingError = messageOf(error);
    }
    shell.setContent(
      subscriptionsPage({ companies: companyBilling, error: billingError }),
    );
  }

  /// A fizetes inditasa: hany helyet vesznek.
  ///
  /// A meglevo kiosztas az ALAPERTELMEZES — a cegadmin jellemzoen bovit, nem
  /// nullarol indul. Kevesebbet is beallithat, azt a Stripe kezeli.
  async function openCheckout(): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }

    // A `numberField` nem fogad kezdoerteket, ezert a mezore irjuk ra.
    // A meglevo kiosztas az ALAPERTELMEZES: a cegadmin jellemzoen bovit, nem
    // nullarol indul.
    const basic = numberField({ label: t('people.seatBasic'), min: 0 });
    basic.input.value = String(subscription?.seats.basic ?? seatUsage?.basic ?? 1);
    const ai = numberField({ label: t('people.seatAi'), min: 0 });
    ai.input.value = String(subscription?.seats.ai ?? seatUsage?.ai ?? 0);

    openDialog({
      title: t('billing.buy'),
      width: '420px',
      body: () => h('div', { class: 'col g12' }, basic.element, ai.element),
      footer: (close) => [
        h('span', { class: 'grow' }),
        h('button', {
          class: 'btn btn-secondary',
          type: 'button',
          text: t('general.cancel'),
          onClick: close,
        }),
        h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: t('billing.buy'),
          onClick: () => {
            const b = Number(basic.input.value) || 0;
            const a = Number(ai.input.value) || 0;
            if (b + a <= 0) {
              basic.setError(t('billing.needSeat'));
              return;
            }
            close();
            shell.toast(t('billing.starting'), 'inf');
            void startCheckout({ companyId: ctx.companyId, basicSeats: b, aiSeats: a })
              .then((url) => {
                // Konverzio a Meta fele (csak hozzajarulassal). Utana rovid
                // varakozas, hogy az esemeny elinduljon, mielott elhagyjuk az
                // oldalt. Hozzajarulas nelkul a varakozas is elmarad.
                const kuldve = metaEsemeny('InitiateCheckout', {
                  currency: 'HUF',
                  num_items: b + a,
                });
                // A Stripe SAJAT feluletere megyunk: kartyaadat soha nem
                // halad at ezen az appon.
                window.setTimeout(() => window.location.assign(url), kuldve ? 400 : 0);
              })
              .catch((error) => {
                shell.toast(t('billing.failed', [messageOf(error)]), 'bad');
              });
          },
        }),
      ],
    });
  }

  /// A diktált szöveg feldolgozása AI-val.
  ///
  /// A hibát NEM nyeljük el: a képernyő hibaállapotba kerül, és az átirat
  /// megmarad, hogy a felhasználó újrapróbálhassa vagy kézzel javíthassa.
  /// A szerver üzenetét változtatás nélkül mutatjuk — ott tudják, mi hiányzik
  /// (nincs AI-s hely, nincs beállítva a hozzáférés, túl sok kérés).
  async function captureWithAi(text: string): Promise<AiProjectDraft> {
    // HOZZAJARULAS-KAPU. Ez az EGYETLEN pont a weben, ahol a felhasznalo
    // szovege elhagyja az appot. Az App Review a mobilt eppen a hianyzo
    // tajekoztatas es engedelykeres miatt utasitotta el (5.1.1(i), 5.1.2(i));
    // ugyanaz a vegpont fut itt is, tehat ugyanaz a kotelezettseg.
    const uid = auth.currentUser?.uid ?? '';
    if (!vanAiHozzajarulas(uid)) {
      const elfogadta = await kerjAiHozzajarulast();
      if (!elfogadta) {
        // A kepernyo ezt sajat hibauzenetkent mutatja meg. Nem technikai hiba:
        // a felhasznalo most mondta, hogy nem keri.
        throw new Error(t('ai.consentRequired'));
      }
      adjAiHozzajarulast(uid);
    }

    const call = httpsCallable<{ text: string }, AiProjectDraft>(
      functions,
      'aiCaptureToProject',
    );
    const result = await call({ text });
    return result.data;
  }

  async function ensureData(): Promise<void> {
    if (!companyId) {
      cache.loading = false;
      cache.error = 'A felhasznalohoz nincs ceg rendelve.';
      return;
    }
    try {
      const [projects, people] = await Promise.all([
        loadProjects({ companyId, userId: sessionUser.uid, isAdmin }),
        loadPeople(companyId),
      ]);
      cache.projects = projects;
      cache.people = people;

      // A torzsadatok hianya nem akadalyozhatja meg a feladatlista
      // megjeleneset, ezert kulon, hibaturon toltjuk oket.
      const [loadedGroups, loadedJobTitles, loadedFields, loadedRoles, loadedTemplates] =
        await Promise.all([
          loadGroups(companyId).catch(() => []),
          loadJobTitles(companyId).catch(() => []),
          loadFieldDefinitions(companyId).catch(() => []),
          loadRoles(companyId).catch(() => ({})),
          loadProjectTemplates(companyId).catch(() => []),
        ]);
      templates = loadedTemplates;
      const ceg = await loadCompany(companyId).catch(() => ({
        maxUser: undefined,
        subscription: undefined,
      }));
      seatLimit = ceg.maxUser;
      subscription = ceg.subscription;
      // A kiosztott helyek szamolasa kulon, hibaturon: enelkul a beallitasok
      // kepernyo egeszben elhasalna egy szamlalasi hiba miatt.
      seatUsage = await loadSeatUsage(companyId).catch(() => undefined);
      notifications = await loadNotifications(sessionUser.uid).catch(() => []);
      shell.setUnread(notifications.some((n) => !n.seen));
      groups = loadedGroups;
      jobTitles = loadedJobTitles;
      // A `requiredInStatus` MAR a beolvasasbol jon (`status` mezo). Korabban
      // itt `undefined`-ra allt, ezert a mezoszerkeszto valasztoja mindig
      // uresen nyilt — akkor is, ha az ertek el volt mentve.
      fields = loadedFields;
      roles = loadedRoles as Record<string, Role>;
    } catch (error) {
      cache.error = error instanceof Error ? error.message : String(error);
    } finally {
      cache.loading = false;
      markPending();
    }
  }

  /// A függő mentés jelölésének átvezetése a betöltött feladatokra (4.2).
  ///
  /// SZÁNDÉKOSAN nem a Firestore `hasPendingWrites` jelzéséből jön: az írás a
  /// projekt EGYETLEN `taskChange` dokumentumát cseréli le, tehát az a jelzés
  /// a projekt minden feladatára igaz lenne — a felhasználó pedig azt látná,
  /// hogy húsz feladat vár mentésre, pedig egyet módosított.
  function markPending(): void {
    for (const project of cache.projects) {
      for (const item of project.tasks) {
        item.pending = pendingTaskIds.has(item.id);
      }
    }
  }

  /// Megvarja, hogy a HELYI iras tenyleg lathato legyen a gyorsitotarban.
  ///
  /// Offline a Firestore altal visszaadott igeret csak a SZERVER
  /// visszaigazolasakor teljesul, tehat nem hasznalhato jelzesnek. A helyi
  /// mutacio viszont azonnal megjelenik a dokumentumon, `hasPendingWrites:
  /// true` alakban — erre iratkozunk fel, egyetlen alkalomra.
  ///
  /// MINDEN iras a `writes.ts`-bol hozzanyul ehhez a dokumentumhoz: vagy
  /// helyben modositja, vagy — uj verzio szuletesekor — `isCurrent: false`-ra
  /// allitja. Ezert eleg ezt az egyet figyelni.
  ///
  /// A hatarido azert kell, hogy egy elmaradt jelzes ne fagyassza be a
  /// kepernyot: lejartakor ugyanugy ujraolvasunk, legfeljebb a regi adattal.
  function waitForLocalWrite(ctx: WriteContext, timeoutMs = 3000): Promise<void> {
    return new Promise((resolve) => {
      const ref = doc(
        db,
        'projects',
        ctx.companyId,
        'projects',
        ctx.projectId,
        'taskChanges',
        ctx.changeId,
      );
      let done = false;
      let unsubscribe: (() => void) | undefined;
      const finish = (): void => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        unsubscribe?.();
        resolve();
      };
      const timer = setTimeout(finish, timeoutMs);
      unsubscribe = onSnapshot(ref, { includeMetadataChanges: true }, (snapshot) => {
        if (snapshot.metadata.hasPendingWrites) finish();
      });
    });
  }

  /// A push allapotanak felderitese (4.3).
  ///
  /// Harom kulon ok van arra, hogy a kapcsolo ne legyen allithato, es
  /// MINDHAROM mast jelent a felhasznalonak: a bongeszo nem tudja, a
  /// felhasznalo letiltotta, vagy a telepitesbol hianyzik a VAPID kulcs.
  async function detectPush(): Promise<void> {
    if (!(await isPushAvailable())) {
      push = { enabled: false, blocked: 'unsupported' };
      return;
    }
    if (!hasVapidKey()) {
      push = { enabled: false, blocked: 'missing-key' };
      return;
    }
    const state = permissionState();
    push = {
      enabled: state === 'granted',
      blocked: state === 'denied' ? 'denied' : undefined,
    };
  }

  async function togglePush(on: boolean): Promise<void> {
    if (on) {
      const outcome = await enablePush(sessionUser.uid);
      push = {
        enabled: outcome === 'ok',
        blocked:
          outcome === 'denied'
            ? 'denied'
            : outcome === 'unsupported'
              ? 'unsupported'
              : outcome === 'missing-key'
                ? 'missing-key'
                : undefined,
      };
      shell.toast(
        outcome === 'ok'
          ? t('push.enabled')
          : outcome === 'denied'
            ? t('push.denied')
            : outcome === 'unsupported'
              ? t('push.unsupported')
              : outcome === 'missing-key'
                ? t('push.missingKey')
                : t('push.failed'),
        outcome === 'ok' ? 'ok' : 'bad',
      );
    } else {
      const removed = await disablePush(sessionUser.uid);
      push = { enabled: removed ? false : (push?.enabled ?? false) };
      shell.toast(removed ? t('push.disabled') : t('push.failed'), removed ? 'ok' : 'bad');
    }
    renderPeople();
  }

  /// A kapcsolat sávjának frissítése. Egy helyen, hogy ne csússzon szét.
  function refreshConnection(): void {
    markPending();
    shell.setConnection({ online: navigator.onLine, pending: pendingTaskIds.size });
  }

  function show(routeId: string): void {
    shell.setActive(routeId);
    // Az emberek keperyzo alnezetei az utvonal masodik szegmensebol jonnek:
    // `#/people/groups`, `#/people/jobTitles`, `#/people/profile`. A profil
    // eddig SEHONNAN nem nyilt — a `peopleView` sosem allt `profile`-ra.
    if (!routeId.startsWith('people')) {
      peopleView = 'list';
    } else {
      const sub = routeId.split('/')[1];
      peopleView =
        sub === 'groups' || sub === 'jobTitles' || sub === 'profile' ? sub : 'list';
    }
    if (routeId !== 'settings') settingsView = 'home';
    const titleKey = routeTitleKeys[routeId];
    shell.setTitle(titleKey ? t(titleKey) : 'BlazeData');
    shell.setDetail(null);

    if (routeId.startsWith('tasks')) {
      const tab = (routeId.split('/')[1] ?? 'open') as TaskTab;
      renderTasks(tab);
      if (cache.loading) {
        void ensureData().then(() => {
          // Csak akkor rajzolunk ujra, ha a felhasznalo kozben nem lepett tovabb.
          if (currentRoute().startsWith('tasks')) renderTasks(tab);
        });
      }
      return;
    }

    if (routeId === 'projects') {
      renderProjects();
      if (cache.loading) void ensureData().then(renderProjects);
      return;
    }

    if (routeId === 'dashboard') {
      renderDashboard();
      if (cache.loading) void ensureData().then(renderDashboard);
      return;
    }

    if (routeId === 'ai') {
      // MELYLINK-VEDELEM. A menupont elrejtese onmagaban nem korlat: a
      // `#/ai` cimet be lehet irni, es egy kollega el is kuldheti linkben.
      // Itt is megallunk, es MEGMONDJUK, mi hianyzik — egy ures vagy hibas
      // kepernyo csak talalgatasra kesztetne.
      if (sessionUser.seatTier !== 'ai') {
        shell.setContent(
          h(
            'div',
            { class: 'col g24', style: { padding: '20px 24px' } },
            h(
              'div',
              { class: 'card pad16 col g8', style: { maxWidth: '520px' } },
              h('span', { class: 'h3', text: t('ai.noSeatTitle') }),
              h('span', { class: 'body-sm tm', text: t('ai.noSeatBody') }),
            ),
          ),
        );
        return;
      }

      shell.setContent(
        aiCapturePage({
          // A feldolgozas SZERVEROLDALON fut (`aiCaptureToProject`), es ott
          // ellenorzi a hely-szintet is: a menupont elrejtese es a melylink-
          // vedelem csak a feluletet zarja, a vegpont kozvetlenul is hivhato
          // lenne. Fizetos funkcionál ez a kulonbseg nem elmeleti.
          onSubmit: (text) => captureWithAi(text),
          // Az AI-javaslatbol keszulo projekt ugyanazt az irast hasznalja,
          // mint a varazslo — nem kulon utat.
          onCreate: (suggestion) =>
            void persistNewProject({
              title: suggestion.title,
              // Az AI-javaslat NEM ad projekt-leirast, csak cimet es
              // feladatokat — nem talalunk ki helyette szoveget.
              description: '',
              tasks: suggestion.tasks.map((task) => ({
                title: task.title,
                priority: task.priority,
              })),
            }),
        }),
      );
      return;
    }

    if (routeId === 'reports') {
      renderReports();
      if (cache.loading) void ensureData().then(renderReports);
      return;
    }

    if (routeId === 'notifications') {
      renderNotifications();
      if (cache.loading) void ensureData().then(renderNotifications);
      return;
    }

    if (routeId.startsWith('people')) {
      renderPeople();
      if (cache.loading) void ensureData().then(renderPeople);
      return;
    }

    if (routeId === 'subscriptions') {
      void renderSubscriptions();
      return;
    }

    if (routeId === 'settings') {
      renderSettings();
      if (cache.loading) void ensureData().then(renderSettings);
      return;
    }

    if (routeId === 'calendar') {
      renderCalendar();
      if (cache.loading) void ensureData().then(renderCalendar);
      return;
    }

    const pendingKey = routeTitleKeys[routeId];
    shell.setContent(comingSoon(pendingKey ? t(pendingKey) : 'BlazeData'));
  }

  function renderProjects(): void {
    if (cache.loading) {
      shell.setContent(comingSoon(t('app.loadingProjects')));
      return;
    }

    const open = openProjectId
      ? cache.projects.find((p) => p.id === openProjectId)
      : undefined;

    if (open) {
      shell.setContent(
        projectDetailPage({
          project: open,
          people: cache.people,
          onBack: () => {
            openProjectId = undefined;
            renderProjects();
          },
          onTaskSelected: (task) => {
            selectedTask = task;
            showDetail(task, 'all');
          },
          onNewTask: () => {
            const target = open.tasks[0];
            if (!target) {
              shell.toast(t('app.addTaskFromList'), 'inf');
              return;
            }
            openNewTaskDialog({
              people: cache.people,
              projectTitle: open.title,
              onSubmit: (input) =>
                void runWrite(target, t('app.opNewTask'), (ctx) =>
                  addTaskToProject(ctx, {
                    title: input.title,
                    description: input.description,
                    priority: priorityValue(input.priority),
                    deadline: input.deadline,
                    assigneeIds: input.assigneeIds,
                  }),
                ),
            });
          },
          onSetRecurrence: () =>
            openRecurrenceDialog({
              projectTitle: open.title,
              // A korabbi komment szerint ez „kulon utemezot is erint
              // (periodicId, scheduledPushes)". Utananezve ez TEVEDES volt: a
              // `scheduledPushes` kollekcio a HATARIDO-emlekeztetokrol szol,
              // nem az ismetlodesrol, es sem ebben a repoban, sem a cloud
              // functionsben nincs olyan utemezo, ami az `rrule`-bol jovobeli
              // projekteket gyartana. A szabaly ma LEIRAS: a lista ebbol
              // jelzi az „Ismetlodo" cimket, a reszletek ebbol mondjak el
              // szoveggel. Ennyit viszont el TUDUNK menteni.
              onSubmit: ({ rrule }) => void persistRecurrence(open, rrule),
            }),
          onPrint: () =>
            shell.setContent(
              printableProject({
                project: open,
                people: cache.people,
                companyName: sessionUser.companyName,
                onExportCsv: (csv, fileName) => downloadCsv(csv, fileName),
              }),
            ),
          onSaveAsTemplate: () =>
            openTemplateEditor({
              templates: templates.map((t) => ({
                id: t.id,
                name: t.name,
                tasks: t.tasks.map((task) => ({ title: task.title, priority: 'mid' as const })),
              })),
              template: {
                id: '',
                name: `${open.title} — sablon`,
                tasks: open.tasks.map((t) => ({
                  title: t.title,
                  priority: t.priority ?? 'mid',
                })),
              },
              jobTitles: [...new Set(cache.people.map((p) => p.jobTitle).filter(Boolean) as string[])],
              onSubmit: (template) => {
                if (!companyId) {
                  shell.toast(t('app.templateMissingCompany'), 'bad');
                  return;
                }
                void (async () => {
                  try {
                    await saveProjectTemplate(db, companyId, {
                      id: template.id,
                      name: template.name,
                      tasks: template.tasks.map((task) => ({ title: task.title })),
                    });
                    templates = await loadProjectTemplates(companyId).catch(() => templates);
                    shell.toast(t('app.templateSaved'), 'ok');
                  } catch (error) {
                    shell.toast(t('app.templateSaveFailed', [messageOf(error)]), 'bad');
                  }
                })();
              },
            }),
        }),
      );
      return;
    }

    shell.setContent(
      projectListPage({
        projects: cache.projects,
        people: cache.people,
        kind: projectFilter,
        onKindChange: (kind) => {
          projectFilter = kind;
          renderProjects();
        },
        onOpen: (project) => {
          openProjectId = project.id;
          renderProjects();
        },
        onNewProject: () =>
          openNewProjectWizard({
            templates: templates.map((t) => ({
              id: t.id,
              name: t.name,
              // A sablontar csak a CIMET es a leirast tarolja; a prioritas
              // feladatonkent a letrehozaskor dol el.
              tasks: t.tasks.map((task) => ({ title: task.title, priority: 'mid' as const })),
            })),
            // A ceg munkakorei mar be vannak toltve; a sablonfeladat
            // szerkesztoje (W19) ezekbol valaszt.
            jobTitles: jobTitles.map((j) => j.name),
            onSubmit: (input) => void persistNewProject(input),
          }),
      }),
    );
  }

  function renderDashboard(): void {
    if (cache.loading) {
      shell.setContent(comingSoon(t('app.loadingDashboard')));
      return;
    }
    shell.setContent(
      dashboardPage({
        tasks: cache.projects.flatMap((p) => p.tasks),
        people: cache.people,
        userName: sessionUser.fullName,
        onTaskSelected: (task) => {
          selectedTask = task;
          showDetail(task, 'all');
        },
        onShowAllTasks: () => {
          window.location.hash = '/tasks/all';
        },
      }),
    );
  }

  function renderReports(): void {
    if (cache.loading) {
      shell.setContent(comingSoon(t('app.loadingReports')));
      return;
    }
    shell.setContent(
      reportsPage({
        projects: cache.projects,
        people: cache.people,
        onExport: (csv) =>
          downloadCsv(csv, 'blazedata-riport-' + new Date().toISOString().slice(0, 10) + '.csv'),
      }),
    );
  }

  /// CSV letöltése. A BOM azért kell, hogy a magyar Excel felismerje az
  /// UTF-8-at — nélküle az ékezetek elromlanak.
  function downloadCsv(csv: string, fileName: string): void {
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    shell.toast(t('app.downloaded'), 'ok');
  }

  /// A törzsadat-írások közös kontextusa.
  ///
  /// Cég nélkül nincs hova írni. Ez a gyakorlatban nem fordul elő (cég nélküli
  /// belépés a hibaoldalra megy), de `null`-t adni és a hívónál kezelni
  /// tisztább, mint felkiáltójellel elhallgattatni a fordítót.
  function masterContext(): MasterDataContext | null {
    return companyId === undefined ? null : { db, companyId };
  }

  /// Csoport mentése, majd a lista frissítése.
  ///
  /// Az írás után ÚJRAOLVASSUK a csoportokat, nem csak a helyi tömböt
  /// piszkáljuk: így egy párhuzamos módosítás sem tűnik el csendben.
  async function persistGroup(
    input: { name: string; userIds: string[] },
    id?: string,
  ): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await saveGroup(ctx, { id, name: input.name, userIds: input.userIds });
      groups = await loadGroups(ctx.companyId);
      shell.toast(id === undefined ? t('app.groupCreated') : t('app.groupSaved'), 'ok');
    } catch (error) {
      shell.toast(t('app.groupSaveFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  /// Saját profilkép feltöltése (W25).
  ///
  /// A méretkorlátot ITT ellenőrizzük, a feltöltés ELŐTT: a Storage is
  /// visszautasítaná, de csak azután, hogy a felhasználó végigvárta a
  /// feltöltést — és a hibaüzenete nem mondaná meg, mi a baj.
  async function uploadOwnPhoto(file: File): Promise<void> {
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      shell.toast(t('app.photoTooLarge'), 'bad');
      return;
    }
    try {
      const url = await uploadProfilePhoto({ db, storage }, sessionUser.uid, file);
      // A helyi másolatot is frissítjük, különben a képernyő újrarajzolásakor
      // a régi kép (vagy a monogram) jönne vissza.
      sessionUser.photoUrl = url;
      shell.toast(t('app.photoUploaded'), 'ok');
    } catch (error) {
      shell.toast(t('app.photoUploadFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  /// Ismétlődés beállítása egy meglévő projekten (W21).
  async function persistRecurrence(
    project: { id: string; currentChangeId?: string },
    rrule: string | undefined,
  ): Promise<void> {
    if (!companyId || !project.currentChangeId) {
      shell.toast(t('app.missingProjectShort'), 'bad');
      return;
    }
    try {
      await setProjectRecurrence(
        {
          db,
          companyId,
          projectId: project.id,
          changeId: project.currentChangeId,
          userId: sessionUser.uid,
        },
        rrule,
      );
      cache.loading = true;
      await ensureData();
      shell.toast(t('app.recurrenceSaved'), 'ok');
    } catch (error) {
      shell.toast(t('app.recurrenceSaveFailed', [messageOf(error)]), 'bad');
    }
    show(currentRoute());
  }

  /// Új projekt létrehozása a varázslóból (W19).
  ///
  /// A sablonfeladatok MUNKAKÖRT hordoznak, nem felelőst — a terv szerint „a
  /// feladat a megadott munkakör tagjaihoz kerül kiosztásra". A felelősöket
  /// ezért a munkakörből származtatjuk; ahol nincs ilyen ember, ott a feladat
  /// felelős NÉLKÜL jön létre. Nem találunk ki felelőst: egy rosszul
  /// kiosztott feladat rosszabb, mint egy kiosztatlan.
  async function persistNewProject(input: NewProjectInput): Promise<void> {
    if (!companyId) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await createProject(
        { db, companyId, userId: sessionUser.uid },
        {
          title: input.title,
          description: input.description,
          tasks: input.tasks.map((task) => ({
            title: task.title,
            jobTitle: task.jobTitle,
            estimateMinutes: task.estimateMinutes,
            priority: priorityValue(task.priority),
            assigneeIds: task.jobTitle
              ? cache.people.filter((p) => p.jobTitle === task.jobTitle).map((p) => p.id)
              : [],
          })),
        },
      );
      cache.loading = true;
      await ensureData();
      shell.toast(t('app.projectCreated'), 'ok');
    } catch (error) {
      shell.toast(t('app.projectCreateFailed', [messageOf(error)]), 'bad');
    }
    show(currentRoute());
  }

  /// Meglévő felhasználó adatainak mentése.
  ///
  /// A csoporttagság KÜLÖN írás: az a csoport dokumentumán él (`userIds`),
  /// nem a felhasználóén. Előbb a felhasználó, aztán a csoportok — így ha a
  /// második elhasal, a név és a szerepkör már jó helyen van, és az újbóli
  /// mentés csak a hiányzó részt pótolja.
  async function persistUser(userId: string, input: UserInput): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await saveUserProfile(db, ctx.companyId, userId, {
        firstName: input.firstName,
        lastName: input.lastName,
        jobTitle: input.jobTitle,
        role: input.role,
      });
      // A hely-szint KÜLÖN, szerveroldalon: az AI-s helyre váltás keretet
      // fogyaszt, és a felület nem tud helyeket számolni.
      if (input.seatTier) {
        await setSeatTier(userAdmin, ctx.companyId, userId, input.seatTier);
      }
      await syncUserGroups(ctx, userId, groups, input.groupIds);

      cache.people = await loadPeople(ctx.companyId);
      groups = await loadGroups(ctx.companyId);
      roles = (await loadRoles(ctx.companyId)) as Record<string, Role>;
      shell.toast(t('app.userSaved'), 'ok');
    } catch (error) {
      shell.toast(t('app.userSaveFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  /// ÚJ felhasználó felvétele.
  ///
  /// Ez eddig a weben nem ment — a felület kiírta, hogy a mobilalkalmazásból
  /// megy. Az akadály valódi volt: a fiók létrehozása BE IS LÉPTET az új
  /// fiókkal, tehát az admin a felvétel pillanatában kiesett volna a saját
  /// munkamenetéből. A `createCompanyUser` ezt egy másodlagos Firebase-appal
  /// kerüli meg, ugyanúgy, ahogy a mobil.
  ///
  /// A csoporttagság a felvétel UTÁN megy át: az azonosítót az Auth adja, és
  /// előtte még nem létezik. Ha a csoportírás hasal el, a felhasználó már
  /// létezik, és a szerkesztőből pótolható — fordítva nem lenne igaz.
  /// A felhasználó-felvétel kapcsolatai. A létrehozás SZERVEROLDALON fut
  /// (`createUserInCompany`), mert a férőhely-keretet csak ott lehet
  /// megszámolni — és így a másodlagos Firebase-app trükk is feleslegessé
  /// vált.
  const userAdmin: UserAdminContext = { db, auth, functions };

  async function createPerson(input: UserInput): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      const userId = await createCompanyUser(userAdmin, ctx.companyId, {
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
        jobTitle: input.jobTitle,
        role: input.role,
      });
      if (input.groupIds.length > 0) {
        await syncUserGroups(ctx, userId, groups, input.groupIds);
      }

      cache.people = await loadPeople(ctx.companyId);
      groups = await loadGroups(ctx.companyId);
      roles = (await loadRoles(ctx.companyId)) as Record<string, Role>;
      shell.toast(t('app.userCreated', [input.email.trim()]), 'ok');
    } catch (error) {
      shell.toast(t('app.userCreateFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  /// A felhasználó eltávolítása a rendszerből.
  ///
  /// A BELÉPÉSI FIÓK megmarad — a megszüntetéséhez admin jogosultság kell,
  /// amivel a böngésző nem rendelkezik. Ezt a megerősítő ablak ki is mondja:
  /// az érintett be tud lépni, de dokumentum híján hibaüzenetet kap. A mobil
  /// ugyanezt teszi, csak nem írja ki.
  async function removePerson(person: PersonView): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await deleteCompanyUser(userAdmin, ctx.companyId, person.id, groups);

      cache.people = await loadPeople(ctx.companyId);
      groups = await loadGroups(ctx.companyId);
      roles = (await loadRoles(ctx.companyId)) as Record<string, Role>;
      shell.toast(t('app.userDeleted', [person.fullName]), 'ok');
    } catch (error) {
      shell.toast(t('app.userDeleteFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  /// Dinamikus mező mentése, majd a lista újraolvasása.
  ///
  /// Az írás után ÚJRAOLVASUNK, nem a helyi tömböt írjuk: a mező
  /// azonosítóját az adatbázis adja, és egy párhuzamos módosítás sem tűnhet
  /// el csendben.
  async function persistField(
    input: Omit<FieldDefinition, 'id'>,
    id?: string,
  ): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await saveField(ctx, { id, ...input });
      fields = await loadFieldDefinitions(ctx.companyId);
      shell.toast(t('app.fieldSaved'), 'ok');
    } catch (error) {
      shell.toast(t('app.fieldSaveFailed', [messageOf(error)]), 'bad');
    }
    show(currentRoute());
  }

  /// Dinamikus mező törlése.
  ///
  /// A már kitöltött ÉRTÉKEK a feladatokon maradnak — azok a feladat saját
  /// dokumentumában élnek, nem itt. A szerkesztő megerősítő kérdése ezt ki
  /// is mondja.
  async function removeField(field: FieldDefinition): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await deleteFieldDefinition(ctx, field.id);
      fields = await loadFieldDefinitions(ctx.companyId);
      shell.toast(t('app.fieldDeleted'), 'ok');
    } catch (error) {
      shell.toast(t('app.fieldDeleteFailed', [messageOf(error)]), 'bad');
    }
    show(currentRoute());
  }

  /// Csoport törlése. Visszafordíthatatlan, ezért a hívó előbb megerősítést kér.
  async function removeGroup(group: { id: string; name: string }): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await deleteGroup(ctx, group.id);
      groups = await loadGroups(ctx.companyId);
      shell.toast(t('app.groupDeleted'), 'ok');
    } catch (error) {
      shell.toast(t('app.groupDeleteFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  async function persistJobTitle(name: string): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await saveJobTitle(ctx, { name });
      jobTitles = await loadJobTitles(ctx.companyId);
      shell.toast(t('app.jobTitleAdded'), 'ok');
    } catch (error) {
      shell.toast(t('app.jobTitleAddFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  async function removeJobTitle(jobTitle: { id: string; name: string }): Promise<void> {
    const ctx = masterContext();
    if (ctx === null) {
      shell.toast(t('app.missingCompany'), 'bad');
      return;
    }
    try {
      await deleteJobTitle(ctx, jobTitle.id);
      jobTitles = await loadJobTitles(ctx.companyId);
      shell.toast(t('app.jobTitleDeleted'), 'ok');
    } catch (error) {
      shell.toast(t('app.jobTitleDeleteFailed', [messageOf(error)]), 'bad');
    }
    renderPeople();
  }

  function renderPeople(): void {
    if (cache.loading) {
      shell.setContent(comingSoon(t('app.loadingPeople')));
      return;
    }

    if (peopleView === 'groups') {
      shell.setContent(
        groupsPage({
          groups,
          people: cache.people,
          onAddMembers: (group) =>
            openPersonPicker({
              title: t('app.addMemberTo', [group.name]),
              people: cache.people,
              openCounts: openTaskCounts(),
              selected: group.userIds,
              // A mentes MAR letezett (`persistGroup`) — ez a parbeszed
              // egyszeruen nem hivta meg, ezert a „Tag hozzaadasa" gomb
              // ugy viselkedett, mintha mentene, kozben semmi nem tortent.
              onSubmit: (userIds) => void persistGroup({ name: group.name, userIds }, group.id),
            }),
          onNew: () =>
            openGroupDialog({
              people: cache.people,
              onSubmit: (input) => void persistGroup(input),
            }),
          onEdit: (group) =>
            openGroupDialog({
              group,
              people: cache.people,
              onSubmit: (input) => void persistGroup(input, group.id),
              // A törlés visszafordíthatatlan, ezért külön megerősítés kell —
              // a szerkesztő ablakban egy félrekattintás is elég lenne.
              onDelete: () =>
                confirmDialog({
                  title: t('app.deleteGroupTitle'),
                  message:
                    t('app.deleteGroupMessage', [group.name]),
                  confirmLabel: t('general.delete'),
                  danger: true,
                  onConfirm: () => void removeGroup(group),
                }),
            }),
        }),
      );
      return;
    }

    if (peopleView === 'jobTitles') {
      shell.setContent(
        jobTitlesPage({
          jobTitles,
          people: cache.people,
          onAdd: (name) => void persistJobTitle(name),
          onRemove: (jobTitle) => void removeJobTitle(jobTitle),
        }),
      );
      return;
    }

    if (peopleView === 'profile') {
      shell.setContent(
        profilePage({
          person: {
            fullName: sessionUser.fullName,
            email: sessionUser.email,
            jobTitle: sessionUser.jobTitle,
            company: sessionUser.companyName,
            // A `roleOf` kisbetus osszefoglalot ad (`super`/`admin`/`user`),
            // a `roleLabels` viszont a TAROLT ertekekkel van kulcsolva.
            role: ({ super: 'SUPER', admin: 'ADMIN', user: 'USER' } as const)[
              roleOf(sessionUser.memberships)
            ],
            photoUrl: sessionUser.photoUrl,
          },
          onPickPhoto: (file) => void uploadOwnPhoto(file),
          theme,
          onThemeChange: (next) => {
            theme = next;
            const dark =
              next === 'dark' ||
              (next === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
            root!.classList.toggle('dark', dark);
            renderPeople();
          },
          // A beallitasok a FELHASZNALO DOKUMENTUMABOL jonnek
          // (`users/{uid}.autoUserPush`), nem bedrotozott ertekekbol. Eddig
          // mindenkinek ugyanaz a harom szam latszott, fuggetlenul attol,
          // mi volt elmentve — a mobilon mast allito felhasznalo a weben a
          // sajatja helyett egy alapertelmezest latott.
          settings: {
            beforeDeadlineDays: sessionUser.autoUserPush?.day ?? 0,
            beforeDeadlineHours: sessionUser.autoUserPush?.hours ?? 1,
            beforeDeadlineMinutes: sessionUser.autoUserPush?.minutes ?? 0,
            afterDeadlineForAssigner: sessionUser.autoUserPush?.pushAfterDeadline ?? true,
          },
          push: push
            ? {
                enabled: push.enabled,
                blocked: push.blocked,
                onChange: (on) => void togglePush(on),
              }
            : undefined,
          onSaveSettings: (next) => {
            void saveAutoUserPush(db, sessionUser.uid, {
              day: next.beforeDeadlineDays,
              hours: next.beforeDeadlineHours,
              minutes: next.beforeDeadlineMinutes,
              pushAfterDeadline: next.afterDeadlineForAssigner,
            })
              .then(() => {
                // A helyi masolatot is frissitjuk, kulonben a kepernyo
                // ujrarajzolasakor a REGI ertekek jonnenek vissza, es ugy
                // tunne, hogy a mentes nem tortent meg.
                sessionUser.autoUserPush = {
                  day: next.beforeDeadlineDays,
                  hours: next.beforeDeadlineHours,
                  minutes: next.beforeDeadlineMinutes,
                  pushAfterDeadline: next.afterDeadlineForAssigner,
                };
                shell.toast(t('app.settingsSaved'), 'ok');
              })
              .catch((error: unknown) =>
                shell.toast(t('app.settingsSaveFailed', [messageOf(error)]), 'bad'),
              );
          },
          // A profil sugoszovege MAR azt igeri, hogy „a jelszo-beallito level
          // a bejelentkezesi cimedre erkezik" — eddig viszont semmi nem ment
          // ki. Ugyanaz a hivas, mint a felhasznalo-szerkesztonel.
          onChangePassword: () => {
            void sendPasswordResetEmail(auth, sessionUser.email)
              .then(() => shell.toast(t('app.resetSent'), 'ok'))
              .catch(() => shell.toast(t('app.resetFailed'), 'bad'));
          },
          onSignOut: () => void signOut(auth),
        }),
      );
      return;
    }

    shell.setContent(
      peoplePage({
        people: cache.people,
        roles,
        groups,
        jobTitleCount: jobTitles.length,
        seatLimit,
        onNewUser: () =>
          openUserDialog({
            jobTitles: jobTitles.map((j) => j.name),
            groups,
            // A felvetel egy MASODLAGOS Firebase-appon megy (lasd
            // `lib/user-admin.ts`), kulonben a `createUserWithEmailAndPassword`
            // az admin munkamenete helyebe leptetne be az uj felhasznalot.
            onSubmit: (input) => void createPerson(input),
          }),
        onEditUser: (person) =>
          openUserDialog({
            person,
            role: roles[person.id],
            jobTitles: jobTitles.map((j) => j.name),
            groups,
            // A csoporttagsag a csoportok `userIds` listajabol olvasodik ki.
            groupIds: groups.filter((g) => g.userIds.includes(person.id)).map((g) => g.id),
            onSendPasswordReset: person.email
              ? () => {
                  void sendPasswordResetEmail(auth, person.email!)
                    .then(() => shell.toast(t('app.resetSent'), 'ok'))
                    .catch(() =>
                      shell.toast(t('app.resetFailed'), 'bad'),
                    );
                }
              : undefined,
            onSubmit: (input) => void persistUser(person.id, input),
            // A BELEPESI FIOK megmarad: a megszuntetesehez admin jogosultsag
            // kell, amivel a kliens nem rendelkezik. A megerosito ablak ezt
            // KIMONDJA — a mobil ugyanezt teszi, csak elhallgatja.
            onDelete: () =>
              confirmDialog({
                title: t('app.deleteUserTitle'),
                message: t('app.deleteUserMessage', [person.fullName]),
                confirmLabel: t('general.delete'),
                danger: true,
                onConfirm: () => void removePerson(person),
              }),
          }),
        onManageGroups: () => {
          window.location.hash = '#/people/groups';
        },
        onManageJobTitles: () => {
          window.location.hash = '#/people/jobTitles';
        },
      }),
    );
  }

  function renderNotifications(): void {
    shell.setContent(
      notificationsPage({
        notifications,
        tab: notificationTab,
        onTabChange: (next) => {
          notificationTab = next;
          renderNotifications();
        },
        onOpen: (notification) => {
          // Megnyitaskor olvasottnak jeloljuk, majd — ha van hozza feladat —
          // atugrunk a feladatlistara.
          void (async () => {
            if (!notification.seen) {
              try {
                await markNotificationsSeen(db, sessionUser.uid, [notification.id]);
                notification.seen = true;
                shell.setUnread(notifications.some((n) => !n.seen));
                renderNotifications();
              } catch {
                // Az olvasottsag jelzese nem kritikus: a tartalom attol meg
                // megnyithato.
              }
            }
            if (notification.taskId) window.location.hash = '#/tasks/all';
          })();
        },
        onMarkAllSeen: () => {
          void (async () => {
            const unread = notifications.filter((n) => !n.seen).map((n) => n.id);
            try {
              await markNotificationsSeen(db, sessionUser.uid, unread);
              for (const item of notifications) item.seen = true;
              shell.setUnread(false);
              renderNotifications();
            } catch (error) {
              shell.toast(t('app.failedWith', [messageOf(error)]), 'bad');
            }
          })();
        },
      }),
    );
  }

  function renderSettings(): void {
    if (settingsView === 'fields') {
      shell.setContent(
        fieldsPage({
          fields,
          onNew: () =>
            openFieldEditor({
              onSubmit: (input) => void persistField(input),
            }),
          onEdit: (field) =>
            openFieldEditor({
              field,
              onSubmit: (input) => void persistField(input, field.id),
              onDelete: () => void removeField(field),
            }),
        }),
      );
      return;
    }

    shell.setContent(
      settingsPage({
        theme,
        companyName: sessionUser.companyName,
        subscription,
        seatUsage,
        // A fizetest CSAK cegadmin inditja. A hivas szerveroldalon is
        // ellenorzi, ezert ez itt kenyelmi szures, nem a korlat.
        onBuySeats: isAdmin ? () => void openCheckout() : undefined,
        // A terv minden sor ala kiirja, mennyi elem van mogotte. Eddig
        // altalanos leiras allt ott, ami minden cegnel ugyanaz volt.
        counts: {
          templates: templates.length,
          fields: fields.length,
          jobTitles: jobTitles.length,
          people: cache.people.length,
          groups: groups.length,
        },
        aiConsentGranted: vanAiHozzajarulas(auth.currentUser?.uid ?? ''),
        onAiConsentToggle: () => {
          const uid = auth.currentUser?.uid ?? '';
          if (vanAiHozzajarulas(uid)) {
            vondVisszaAiHozzajarulast(uid);
            renderSettings();
            return;
          }
          // Megadni csak ugyanazzal a tájékoztatással lehet, mint az első
          // használatkor — nem lehet „véletlenül" bekapcsolni.
          void kerjAiHozzajarulast().then((elfogadta) => {
            if (elfogadta) adjAiHozzajarulast(uid);
            renderSettings();
          });
        },
        onThemeChange: (next) => {
          theme = next;
          // A rendszer-beallitas kovetese az alapertelmezes; a kezi valasztas
          // felulirja, de csak amig az oldal nyitva van.
          const dark =
            next === 'dark' ||
            (next === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
          root!.classList.toggle('dark', dark);
          renderSettings();
        },
        onOpenTemplates: () =>
          // A sablontar (W22) mostmar valodi: a beallitasokbol is nyithato.
          openTemplateEditor({
            templates: templates.map((t) => ({
              id: t.id,
              name: t.name,
              tasks: t.tasks.map((task) => ({ title: task.title, priority: 'mid' as const })),
            })),
            jobTitles: jobTitles.map((j) => j.name),
            onSubmit: (template) => {
              if (!companyId) {
                shell.toast(t('app.templateMissingCompany'), 'bad');
                return;
              }
              void (async () => {
                try {
                  await saveProjectTemplate(db, companyId, {
                    id: template.id,
                    name: template.name,
                    tasks: template.tasks.map((task) => ({ title: task.title })),
                  });
                  templates = await loadProjectTemplates(companyId).catch(() => templates);
                  shell.toast(t('app.templateSaved'), 'ok');
                  renderSettings();
                } catch (error) {
                  shell.toast(t('app.templateSaveFailed', [messageOf(error)]), 'bad');
                }
              })();
            },
          }),
        onOpenFields: () => {
          settingsView = 'fields';
          renderSettings();
        },
        onOpenJobTitles: () => {
          window.location.hash = '#/people/jobTitles';
        },
        onOpenGroups: () => {
          window.location.hash = '#/people/groups';
        },
      }),
    );
  }

  function renderCalendar(): void {
    if (cache.loading) {
      shell.setContent(comingSoon(t('app.loadingCalendar')));
      return;
    }
    shell.setContent(
      calendarPage({
        mode: calendarMode,
        anchor: calendarAnchor,
        tasks: cache.projects.flatMap((p) => p.tasks),
        onModeChange: (mode) => {
          calendarMode = mode;
          renderCalendar();
        },
        onAnchorChange: (anchor) => {
          calendarAnchor = anchor;
          renderCalendar();
        },
        onAddOnDay: (date) => startNewTask(date),
        onSelect: (task) => {
          selectedTask = task;
          showDetail(task, 'all');
        },
      }),
    );
  }

  shell = createShell({
    items: [
      {
        id: 'dashboard',
        label: t('nav.dashboard'),
        iconPath: 'M2.8 8.6h4.2V3.2H2.8zM9 12.8h4.2V3.2H9zM2.8 12.8h4.2v-2.4H2.8z',
      },
      ...defaultNavItems({ aiSeat: sessionUser.seatTier === 'ai' }),
      // Az elofizetes-attekinto CSAK szuperadminnak keszul el. A szabaly is
      // csak neki engedi a cegek listazasat, tehat masnak a menupont egy
      // biztosan elhasalo kepernyore vinne.
      ...(isSuper
        ? [
            {
              id: 'subscriptions',
              label: t('billing.overviewTitle'),
              iconPath: 'M2.4 4.4h11.2v7.2H2.4zM2.4 6.8h11.2M4.8 9.6h2.4',
            },
          ]
        : []),
    ],
    activeId: currentRoute(),
    user: {
      name: sessionUser.fullName,
      email: sessionUser.email,
      company: sessionUser.companyName,
    },
    onNavigate: (id) => {
      // Az útvonal a címsorban is látszik: megosztható és visszalépéssel működik.
      window.location.hash = `/${id}`;
    },
    onOpenProfile: () => {
      window.location.hash = '#/people/profile';
    },
    onOpenNotifications: () => {
      window.location.hash = '#/notifications';
    },
    onSignOut: () => void signOut(auth),
  });

  // Ctrl+K globalis kereso. A forrast fuggvenykent adjuk at, hogy
  // ujranyitaskor a friss adatot lassa, ne a betolteskorit.
  const search: SearchOverlayHandle = searchOverlay({
    sources: () => ({
      tasks: cache.projects.flatMap((p) => p.tasks),
      projects: cache.projects,
      people: cache.people,
    }),
    onSelect: (hit) => {
      if (hit.kind === 'task') {
        selectedTask = hit.task;
        showDetail(hit.task, 'all');
      } else if (hit.kind === 'project') {
        openProjectId = hit.project.id;
        window.location.hash = '#/projects';
      } else if (hit.kind === 'file') {
        // A fajl-talalat a FELADATOT nyitja meg: onnan tolthet le, es ott
        // latszik a szovegkornyezete is.
        selectedTask = hit.task;
        showDetail(hit.task, 'all');
      } else {
        window.location.hash = '#/people';
      }
    },
  });
  void search;

  render(shell.root);
  refreshConnection();
  show(currentRoute());

  // 4.3 — a push allapota es az elotérben erkezo uzenetek.
  //
  // A felderites nem blokkolja az indulast: a profil kartyaja addig nem
  // jelenik meg, amig ki nem derult, mihez van jogunk.
  void detectPush().then(() => {
    if (currentRoute() === 'people/profile') renderPeople();
  });
  onPushInForeground((message) => {
    // Elotérben a rendszer NEM mutat ertesitest — a lap kapja meg. Rovid
    // visszajelzest adunk, es frissitjuk az ertesitesek listajat, hogy a
    // csengo szamlaloja ne csusszon el a valosagtol.
    shell.toast(`${message.title}${message.body ? ` — ${message.body}` : ''}`, 'inf');
    void loadNotifications(sessionUser.uid)
      .then((list) => {
        notifications = list;
        shell.setUnread(notifications.some((n) => !n.seen));
        if (currentRoute() === 'notifications') show('notifications');
      })
      .catch(() => undefined);
  });

  window.addEventListener('hashchange', () => show(currentRoute()));

  // 4.2 — a kapcsolat változása.
  //
  // Offline csak a sáv jelenik meg; a lista a helyi tárból marad használható.
  // Visszatéréskor a Firestore magától felküldi a sorban álló írásokat;
  // a `waitForPendingWrites` akkor teljesül, amikor MIND felment — ekkor
  // töltünk újra, hogy a felület a szerver igazságát mutassa.
  window.addEventListener('offline', refreshConnection);
  window.addEventListener('online', () => {
    refreshConnection();
    const had = pendingTaskIds.size > 0;
    void waitForPendingWrites(db)
      .then(async () => {
        if (had) shell.toast(t('app.allSynced'), 'ok');
        cache.loading = true;
        await ensureData();
        refreshConnection();
        show(currentRoute());
      })
      .catch(() => {
        // A varakozas megszakadhat (pl. ujabb kapcsolatvesztes); ilyenkor a
        // kovetkezo `online` esemeny ujra elindítja.
      });
  });
}

/// Fejlesztői előnézet: a váz és a képernyők kipróbálása bejelentkezés és élő
/// adat nélkül. CSAK fejlesztői buildben él — a Vite a produkciós csomagból
/// kihagyja, mert az `import.meta.env.DEV` ott hamis, és az ág eltűnik.
///
/// Erre azért van szükség, mert a képernyők belépés mögött vannak: enélkül
/// minden apró elrendezés-változtatáshoz éles adatra és jelszóra lenne szükség.
function tryDevPreview(): boolean {
  if (!import.meta.env.DEV) return false;
  if (!window.location.hash.startsWith('#/dev')) return false;

  // A minta-adat csak itt kerul be; a produkcios csomagbol a Vite kihagyja.
  void (async () => {
    const { devPeople, devPendingTaskIds, devProjects, devUserId } = await import(
      './dev/fixtures'
    );
    mountApp(
      {
        uid: devUserId,
        email: 'fejlesztoi@elonezet.local',
        fullName: 'Fejlesztői Előnézet',
        memberships: [{ companyId: 'dev', role: 'ADMIN' }],
        companyName: 'Minta Kft.',
      },
      { projects: devProjects, people: devPeople, pendingTaskIds: devPendingTaskIds },
    );
  })();
  return true;
}

render(loadingScreen());

// A szotar a render ELOTT toltodik be (4.1). A betoltokepernyo addig is
// latszik; ha a szotar nem jon meg, a `t()` a kulcsot adja vissza — az
// azonnal lathato hiba, nem csendes felrecsuszas.
//
// A betoltes ASZINKRON FUGGVENYBEN all, nem modul szintu `await`-tel: a
// Vite alapertelmezett cel-bongeszoi (chrome87, safari14, firefox78) nem
// ismerik a modul szintu `await`-et, es a PRODUKCIOS BUILD elhasal tole.
// A `tsc --noEmit` ezt nem latja — csak a `vite build`.
void (async () => {
  // A NYELV NEM FIX TOBBE. Eddig itt `'hu'` allt, tehat az angol szotar
  // (82 KB, karbantartva) soha nem jelent meg — a web MINDENKINEK magyar volt.
  // A `detectLanguage()` sorrendje: a latogato korabbi valasztasa, utana a
  // bongeszo nyelve, minden mason angol. Ugyanaz a szabaly, mint a mobilon.
  await loadDictionary(detectLanguage());
  startApp();
})();

function startApp(): void {
if (!tryDevPreview()) {
window.addEventListener('hashchange', () => {
  // Bejelentkezes elott a nyitooldal <-> belepes <-> jelszo valtas is hash-en
  // megy; bejelentkezve a vaz sajat figyeloje kezeli.
  if (!auth.currentUser) renderSignedOut();
});

// A suti-banner CSAK akkor jelenik meg, ha van bekotott, hozzajarulashoz
// kotott eszkoz. A Meta pixel ilyen (marketing), ezert a bekotese a banner
// ELOTT tortenik: a banner ebbol tudja, hogy van mire hozzajarulast kerni.
// TISZTA UTVONAL A JOGI OLDALAKHOZ, HASH NELKUL.
//
// A belso navigacio tovabbra is hash-alapu, de a KIFELE adott cimek nem
// lehetnek azok. Az App Store Connectben megadott adatkezelesi cim
// `#/adatkezeles` volt; ha a birala eszkoze levagja a fragmentumot, a
// nyitooldalra erkezik, es nem latja a szakaszt, amire hivatkozunk. Ugyanez
// all a keresokre es minden olyan rendszerre, ami csak az utvonalat tarolja.
//
// A Hosting minden utvonalat az index.html-re iranyit (lasd firebase.json),
// igy itt eleg a bejovo utvonalat hash-re forditani, a render ELOTT: a
// router mar a helyes hash-t latja, hashchange esemeny nelkul is.
(() => {
  const ut = window.location.pathname.replace(/\/+$/, '');
  const terkep: Record<string, string> = {
    '/adatkezeles': '#/adatkezeles',
    '/aszf': '#/aszf',
    '/kapcsolat': '#/kapcsolat',
  };
  const cel = terkep[ut];
  if (!cel) return;
  // A query a hash MOGE kerul, mert a kapcsolat oldal onnan olvassa
  // (`#/kapcsolat?tipus=hiba`).
  window.history.replaceState(null, '', `/${cel}${window.location.search}`);
})();

// A GA4 a Statisztika-, a Meta pixel a Marketing-hozzajarulashoz kotott;
// mindkettot a banner ELOTT kell bekotni.
gaBekotes();
metaPixelBekotes();
sutiBannerMegjelenites();
appBannerMegjelenites();

function renderSignedOut(): void {
  const hash = window.location.hash;
  if (hash.startsWith('#/jelszo')) {
    render(
      passwordResetPage({
        auth,
        onBack: () => {
          window.location.hash = '/belepes';
        },
      }),
    );
    return;
  }
  // A JOGI OLDALAK ES A KAPCSOLAT KIJELENTKEZVE IS ELERHETOK, es kulon
  // utvonalon — nem a nyitooldal horgonyaikent. Igy onalloan hivatkozhatok:
  // az App Store tamogatasi cimehez, a keresokhoz es a lablechez is ez kell.
  if (hash.startsWith('#/aszf')) {
    render(termsPage());
    return;
  }
  if (hash.startsWith('#/adatkezeles')) {
    render(privacyPage());
    return;
  }
  if (hash.startsWith('#/kapcsolat')) {
    // A `#/kapcsolat?tipus=hiba` EGYENESEN a hibabejelentesre nyit. A
    // nyitooldalrol igy kulon utat lehet kinalni a „hibat talaltam" esetre —
    // enelkul a bejelento a kapcsolatfelvetel urlapjan kot ki, es neki kell
    // megtalalnia a masik fulet.
    render(
      contactPage({
        kezdoTipus: /[?&]tipus=hiba\b/.test(hash) ? 'hiba' : 'kapcsolat',
        onBack: () => {
          window.location.hash = '/';
        },
      }),
    );
    return;
  }
  if (hash.startsWith('#/regisztracio')) {
    render(
      registerPage({
        auth,
        db,
        onLogin: () => {
          window.location.hash = '/belepes';
        },
      }),
    );
    return;
  }
  render(
    hash.startsWith('#/belepes')
      ? loginPage(auth, {
          onRegister: () => {
            window.location.hash = '/regisztracio';
          },
        })
      : landingPage({
          onLogin: () => {
            window.location.hash = '/belepes';
          },
          onRegister: () => {
            window.location.hash = '/regisztracio';
          },
        }),
  );
}

onAuthStateChanged(auth, (user: User | null) => {
  if (!user) {
    renderSignedOut();
    return;
  }

  render(loadingScreen());

  void (async () => {
    render(authLoadingPage());
    const sessionUser = await loadSessionUser(user);
    const firstCompany = sessionUser.memberships[0]?.companyId;

    if (!firstCompany) {
      // Ceg nelkul a feladatlista ures lenne, magyarazat nelkul — inkabb
      // megmondjuk, mi a helyzet, es adunk kiutat.
      render(errorPage({
        kind: 'no-company',
        // A terv adatkartyaja a belepett cimet is mutatja: tamogatasnal ebbol
        // derul ki, MELYIK fiokkal van a baj.
        email: auth.currentUser?.email ?? undefined,
        onSignOut: () => void signOut(auth),
      }));
      return;
    }

    sessionUser.companyName = (await loadCompany(firstCompany)).name;
    mountApp(sessionUser);
  })();
});
}
}
